import { useEffect, useState } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { type EmailTemplate } from '@/models/emailTemplate'
import { NewTemplateButton } from './NewTemplateButton'
import { TemplateEditor } from './TemplateEditor'
import { TemplateList } from './TemplateList'

interface TemplatesPaneProps {
  templates: EmailTemplate[]
  isLoading: boolean
  /** Whether these templates can also be deleted or made the rep's default. */
  canManage: boolean
  emptyText: string
  /** Offers New template, creating a global or a personal one. */
  create?: { isGlobal: boolean }
  /** Sits above the list, e.g. the Others tab's teammate picker. */
  toolbar?: React.ReactNode
}

export function TemplatesPane({
  templates,
  isLoading,
  canManage,
  emptyText,
  create,
  toolbar,
}: TemplatesPaneProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Nothing picked yet, or the pick was deleted: open the rep's default, else the first.
  const selected =
    templates.find((t) => t.id === selectedId) ??
    templates.find((t) => t.isDefault) ??
    templates.at(0) ??
    null

  // Pin what opened by default, so a re-sort after saving a rename doesn't switch templates.
  useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id)
  }, [selected, selectedId])

  return (
    <Card>
      <CardContent className="grid min-h-[600px] items-start gap-6 lg:grid-cols-[300px_1fr]">
        <div className="flex flex-col gap-3">
          {create && <NewTemplateButton isGlobal={create.isGlobal} onCreated={setSelectedId} />}
          {toolbar}
          {isLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : templates.length === 0 ? (
            <p className="text-muted-foreground text-sm">{emptyText}</p>
          ) : (
            <TemplateList templates={templates} selectedId={selected?.id ?? null} onSelect={setSelectedId} />
          )}
        </div>
        <TemplateEditor key={selected?.id ?? 'empty'} template={selected} canManage={canManage} />
      </CardContent>
    </Card>
  )
}
